import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { Badge } from "components/ui/badge";

/**
 * MultiSelect – Shadcn Combobox-based multi-select.
 *
 * Props:
 *   options    – Array of { value: string, label: string }
 *   value      – Array of selected values (string[])
 *   onChange   – Callback (newValues: string[]) => void
 *   placeholder – Placeholder text when nothing selected
 *   searchPlaceholder – Placeholder for the search input
 *   className  – Extra classes for the trigger button
 *   maxDisplay – Max number of badges to show before collapsing (default 3)
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className,
  maxDisplay = 3,
}) => {
  const [open, setOpen] = React.useState(false);

  const toggle = (optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const removeOne = (e, optionValue) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-auto min-h-9 w-full justify-between bg-white border-gray-200 text-sm hover:bg-gray-50/50 font-normal px-3 py-1.5",
            className
          )}
        >
          <span className="flex flex-wrap gap-1 flex-1 min-w-0">
            {selectedLabels.length === 0 && (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            {selectedLabels.slice(0, maxDisplay).map((label, i) => (
              <Badge
                key={value[i]}
                variant="secondary"
                className="text-xs gap-1 pr-1 rounded-sm font-normal"
              >
                {label}
                <button
                  type="button"
                  onClick={(e) => removeOne(e, value[i])}
                  className="ml-0.5 rounded-sm hover:bg-black/10"
                >
                  <X size={10} />
                </button>
              </Badge>
            ))}
            {selectedLabels.length > maxDisplay && (
              <Badge
                variant="secondary"
                className="text-xs rounded-sm font-normal"
              >
                +{selectedLabels.length - maxDisplay} more
              </Badge>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => toggle(option.value)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value.includes(option.value)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { MultiSelect };
